<div class="zip-preview-root" [class.zip-preview-root--detached]="detachedPreview">
  <div *ngIf="loading" class="zip-preview-state">
    <div class="zip-preview-state__loader" aria-hidden="true">
      <span></span>
      <span></span>
      <span></span>
    </div>
    <div class="zip-preview-state__copy">
      <strong>Unpacking archive</strong>
      <span>Preparing the file explorer and previews.</span>
    </div>
  </div>

  <div *ngIf="!loading && error" class="zip-preview-state zip-preview-state--error">
    <i class="fa-regular fa-file-circle-xmark"></i>
    <span>{{ error }}</span>
  </div>

  <div *ngIf="!loading && !error"
       class="zip-preview-shell"
       [class.zip-preview-shell--detached]="detachedPreview"
      [class.zip-preview-shell--sidebar-rail]="isExplorerRail()"
       [class.zip-preview-shell--sidebar-hidden]="isExplorerHidden()"
       [style.--zip-preview-sidebar-width.px]="getResolvedExplorerWidth()">
    <section class="zip-preview-viewer" *ngIf="selectedFile as file; else emptySelectionTemplate">
      <div *ngIf="!detachedPreview" class="zip-preview-viewer__header">
        <div class="zip-preview-viewer__meta">
          <strong>{{ file.name }}</strong>
          <span>{{ file.path }}</span>
          <span>{{ formatBytes(file.sizeBytes) }} • {{ file.mimeType }}</span>
        </div>

        <div *ngIf="canTogglePreviewMode(file)" class="zip-preview-viewer__tabs">
          <button type="button"
                  class="zip-preview-viewer__tab"
                  [class.zip-preview-viewer__tab--active]="previewMode === 'preview'"
                  (click)="togglePreviewMode('preview')">
            Preview
          </button>
          <button type="button"
                  class="zip-preview-viewer__tab"
                  [class.zip-preview-viewer__tab--active]="previewMode === 'code'"
                  (click)="togglePreviewMode('code')">
            Code
          </button>
        </div>
      </div>

      <div *ngIf="selectedFileLoading" class="zip-preview-state zip-preview-state--compact">
        <div class="zip-preview-state__loader" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div class="zip-preview-state__copy">
          <strong>Loading file</strong>
          <span>Rendering the selected file view.</span>
        </div>
      </div>

      <div *ngIf="!selectedFileLoading && selectedFileError" class="zip-preview-state zip-preview-state--error zip-preview-state--compact">
        <i class="fa-regular fa-file-circle-xmark"></i>
        <span>{{ selectedFileError }}</span>
      </div>

      <div *ngIf="!selectedFileLoading && !selectedFileError" class="zip-preview-viewer__body">
        <div *ngIf="file.truncated" class="zip-preview-notice">
          Showing the first 1 MB of this file in the code viewer.
        </div>

        <ng-container [ngSwitch]="getViewerMode(file)">
          <app-html-previewer *ngSwitchCase="'html'"
                               [html]="file.previewText || file.rawText || ''">
          </app-html-previewer>

          <div *ngSwitchCase="'markdown'" class="zip-preview-markdown"
               [innerHTML]="parseMarkdown(file.rawText || '')">
          </div>

          <div *ngSwitchCase="'csv'" class="zip-preview-csv">
            <div *ngIf="getCsvRows(file).length === 0" class="zip-preview-state zip-preview-state--compact">
              <span>This CSV is empty.</span>
            </div>

            <div *ngIf="getCsvRows(file).length > 0" class="zip-preview-table-wrap">
              <table class="zip-preview-table">
                <thead>
                  <tr>
                    <th *ngFor="let cell of getCsvHeader(file)">{{ cell }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of getCsvBody(file)">
                    <td *ngFor="let cell of row">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div *ngSwitchCase="'image'" class="zip-preview-media-wrap">
            <img *ngIf="file.previewUrl"
                 class="zip-preview-image"
                 [src]="file.previewUrl"
                 [alt]="file.name">
          </div>

          <div *ngSwitchCase="'audio'" class="zip-preview-media-wrap">
            <audio *ngIf="file.previewUrl"
                   class="zip-preview-audio"
                   controls
                   preload="metadata"
                   [src]="file.previewUrl">
            </audio>
          </div>

          <div *ngSwitchCase="'video'" class="zip-preview-media-wrap">
            <video *ngIf="file.previewUrl"
                   class="zip-preview-video"
                   controls
                   preload="metadata"
                   [src]="file.previewUrl">
            </video>
          </div>

          <ngx-monaco-editor *ngSwitchCase="'code'"
                             class="zip-preview-editor"
                             [options]="editorOptions"
                             [ngModel]="getSelectedFileContents()"
                             (ngModelChange)="onEditorChange($event)">
          </ngx-monaco-editor>

          <div *ngSwitchDefault class="zip-preview-state zip-preview-state--compact">
            <i class="fa-regular fa-file-lines"></i>
            <span>This file type does not have an inline preview yet.</span>
          </div>
        </ng-container>
      </div>
    </section>

    <div *ngIf="!isExplorerHidden()"
         class="zip-preview-divider"
         [class.zip-preview-divider--active]="isExplorerResizing"
          [attr.aria-label]="'Resize file explorer'"
          [attr.aria-valuenow]="getResolvedExplorerWidth()"
           [attr.aria-valuemin]="68"
         [attr.aria-valuemax]="420"
         aria-orientation="vertical"
         role="separator"
         tabindex="0"
          (pointerdown)="onExplorerDividerPointerDown($event)"
         (keydown)="onExplorerDividerKeydown($event)">
      <span class="zip-preview-divider__line" aria-hidden="true"></span>

      <button type="button"
              class="zip-preview-divider__toggle"
              aria-label="Hide file explorer"
              title="Hide file explorer"
              (pointerdown)="onExplorerEdgeButtonPointerDown($event)"
              (click)="hideExplorer($event)">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>

    <div *ngIf="isExplorerHidden()" class="zip-preview-reveal">
      <span class="zip-preview-reveal__line" aria-hidden="true"></span>
      <button type="button"
              class="zip-preview-reveal__button"
              aria-label="Show file explorer"
              title="Show file explorer"
              (pointerdown)="onExplorerEdgeButtonPointerDown($event)"
              (click)="showExplorer($event)">
        <i class="fa-solid fa-chevron-left"></i>
      </button>
    </div>

    <aside *ngIf="!isExplorerHidden()"
           class="zip-preview-sidebar"
           [class.zip-preview-sidebar--rail]="isExplorerRail()">
      <div class="zip-preview-sidebar__summary">
        <div class="zip-preview-sidebar__summary-header">
          <div class="zip-preview-sidebar__summary-copy" *ngIf="!isExplorerRail()">
            <strong>{{ detachedPreview ? 'Files' : archiveTitle }}</strong>
            <span>{{ fileCount | number }} files</span>
          </div>

          <div class="zip-preview-sidebar__summary-actions">
            <div class="zip-preview-sidebar__add-wrap">
              <button type="button"
                      class="zip-preview-sidebar__icon-button zip-preview-sidebar__icon-button--danger"
                      aria-label="Delete archive"
                      title="Delete archive"
                      [disabled]="!canDeleteArchive()"
                      (click)="openArchiveDeletePopover($event)">
                <i class="fa-regular fa-trash-can"></i>
              </button>
            </div>

            <div class="zip-preview-sidebar__add-wrap">
              <button type="button"
                      class="zip-preview-sidebar__icon-button"
                      aria-label="Add archive item"
                      title="Add archive item"
                      (click)="openAddMenu($event)">
                <i class="fa-regular fa-plus"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="zip-preview-tree">
        <ng-container *ngTemplateOutlet="treeTemplate; context: { $implicit: treeNodes }"></ng-container>

        <div class="zip-preview-tree__root-drop"
             [class.zip-preview-tree__root-drop--visible]="isDraggingNode()"
             [class.zip-preview-tree__root-drop--active]="isDropDirectoryActive('')"
             (dragover)="onRootDragOver($event)"
             (dragleave)="onRootDragLeave()"
             (drop)="onRootDrop($event)">
          {{ getRootDropHintLabel() }}
        </div>
      </div>

      <div class="zip-preview-sidebar__footer">
        <div class="zip-preview-sidebar__status" [ngClass]="getArchiveStatusClass()">
          {{ getArchiveStatusLabel() }}
        </div>

        <div *ngIf="!detachedPreview || (selectedFile && canTogglePreviewMode(selectedFile))" class="zip-preview-sidebar__footer-actions">
          <button *ngIf="!detachedPreview"
                  type="button"
                  class="zip-preview-sidebar__save zip-preview-sidebar__save--primary"
                  [class.zip-preview-sidebar__save--hidden]="!hasPendingArchiveChanges()"
                  [disabled]="archiveSaving || !hasPendingArchiveChanges()"
                  [attr.tabindex]="hasPendingArchiveChanges() ? 0 : -1"
                  [attr.aria-label]="isExplorerRail() ? 'Save archive changes' : null"
                  [attr.title]="isExplorerRail() ? 'Save archive changes' : null"
                  (click)="saveArchive()">
            <i *ngIf="archiveSaving" class="fa-solid fa-spinner fa-spin"></i>
            <i *ngIf="!archiveSaving" class="fa-regular fa-floppy-disk"></i>
            <span>Save Changes</span>
          </button>

          <button type="button"
                  *ngIf="detachedPreview && selectedFile && canTogglePreviewMode(selectedFile)"
                  class="zip-preview-sidebar__mode-button"
                  [class.zip-preview-sidebar__mode-button--active]="previewMode === 'preview'"
                  [attr.aria-pressed]="previewMode === 'preview'"
                  [attr.title]="isExplorerRail() ? 'Preview' : null"
                  (click)="togglePreviewMode('preview')">
            <i class="fa-regular fa-eye"></i>
            <span>Preview</span>
          </button>
          <button type="button"
              *ngIf="detachedPreview && selectedFile && canTogglePreviewMode(selectedFile)"
                  class="zip-preview-sidebar__mode-button"
                  [class.zip-preview-sidebar__mode-button--active]="previewMode === 'code'"
                  [attr.aria-pressed]="previewMode === 'code'"
                  [attr.title]="isExplorerRail() ? 'Code' : null"
                  (click)="togglePreviewMode('code')">
            <i class="fa-regular fa-file-code"></i>
            <span>Code</span>
          </button>
        </div>
      </div>
    </aside>
  </div>

  <ng-template #emptySelectionTemplate>
    <section class="zip-preview-viewer zip-preview-viewer--empty">
      <div *ngIf="selectedFolderPath; else noSelectionTemplate" class="zip-preview-folder-card">
        <strong>{{ getSelectedFolderName() }}</strong>
        <span>{{ selectedFolderPath }}</span>
        <p>Drag files or folders here to reorganize the archive, or use the row actions to rename and delete items.</p>
      </div>
    </section>
  </ng-template>

  <ng-template #noSelectionTemplate>
    <div class="zip-preview-state zip-preview-state--compact">
      <i class="fa-regular fa-folder-open"></i>
      <span>Select a file from the archive explorer to preview it.</span>
    </div>
  </ng-template>

  <ng-template #treeTemplate let-nodes>
    <div *ngFor="let node of nodes; trackBy: trackByTreeNode" class="zip-tree-node">
      <div class="zip-tree-node__row"
           [class.zip-tree-node__row--active]="isActiveNode(node)"
           [class.zip-tree-node__row--drop-target]="isDropHoverNode(node)"
           [class.zip-tree-node__row--dragging]="draggedNodePath === node.path"
           (dragover)="onNodeDragOver(node, $event)"
           (dragleave)="onNodeDragLeave(node, $event)"
           (drop)="onNodeDrop(node, $event)">
        <button type="button"
                class="zip-tree-node__button"
                [class.zip-tree-node__button--active]="isActiveNode(node)"
              [style.padding-left.px]="getTreeNodeIndent(node)"
              [attr.aria-label]="node.path"
              [attr.title]="isExplorerRail() ? null : node.path"
                draggable="true"
                (mouseenter)="onTreeNodeTooltipEnter(node, $event)"
                (mouseleave)="onTreeNodeTooltipLeave()"
                (focus)="onTreeNodeTooltipEnter(node, $event)"
                (blur)="onTreeNodeTooltipLeave()"
                (dragstart)="onTreeDragStart(node, $event)"
                (dragend)="onTreeDragEnd()"
                (click)="onTreeNodeClick(node)">
          <span class="zip-tree-node__icon">
            <i *ngIf="node.type === 'folder'" class="fa-regular" [ngClass]="node.expanded ? 'fa-folder-open' : 'fa-folder'"></i>
            <i *ngIf="node.type === 'file'" class="fa-regular fa-file-code"></i>
          </span>
          <span class="zip-tree-node__label">{{ node.name }}</span>
        </button>

        <div *ngIf="isActiveNode(node) && !isExplorerRail()" class="zip-tree-node__actions">
          <button type="button"
                  class="zip-tree-node__icon-button"
                  aria-label="Rename archive item"
              draggable="false"
              (mousedown)="$event.stopPropagation()"
                  (click)="openRenamePopover(node, $event)">
            <i class="fa-regular fa-pen-to-square"></i>
          </button>
          <button type="button"
                  class="zip-tree-node__icon-button zip-tree-node__icon-button--danger"
                  aria-label="Delete archive item"
              draggable="false"
              (mousedown)="$event.stopPropagation()"
                  (click)="openDeletePopover(node, $event)">
            <i class="fa-regular fa-trash-can"></i>
          </button>
        </div>
      </div>

      <div *ngIf="isDraggingNode() && isDropHoverNode(node)" class="zip-tree-node__drop-hint">
        {{ getDropHintLabelForNode(node) }}
      </div>

      <ng-container *ngIf="node.type === 'folder' && node.expanded">
        <ng-container *ngTemplateOutlet="treeTemplate; context: { $implicit: node.children }"></ng-container>
      </ng-container>
    </div>
  </ng-template>

  <div *ngIf="isExplorerResizing" class="zip-preview-drag-shield" aria-hidden="true"></div>

  <ng-template #addMenuPopoverTemplate>
    <div class="zip-add-popover-menu">
      <button type="button"
              class="zip-add-popover-menu__item"
              (click)="createRootFile($event)">
        <i class="fa-regular fa-file"></i>
        <span>New file</span>
      </button>
      <button type="button"
              class="zip-add-popover-menu__item"
              (click)="createRootFolder($event)">
        <i class="fa-regular fa-folder"></i>
        <span>New folder</span>
      </button>
    </div>
  </ng-template>

  <ng-template #archiveDeletePopoverTemplate>
    <div class="zip-delete-popover">
      <label class="zip-delete-popover__label">Delete archive?</label>
      <p class="zip-delete-popover__copy">
        Delete {{ archiveTitle }} from storage and remove it from this thread. Files inside the archive will no longer be available.
      </p>
      <div class="zip-delete-popover__actions">
        <button type="button"
                class="zip-delete-popover__button zip-delete-popover__button--ghost"
                (click)="closeArchiveDeletePopover()">
          Cancel
        </button>
        <button type="button"
                class="zip-delete-popover__button zip-delete-popover__button--danger"
                (click)="confirmDeleteArchive($event)">
          Delete
        </button>
      </div>
    </div>
  </ng-template>

  <ng-template #renamePopoverTemplate>
    <form class="zip-rename-popover" (submit)="submitRename(undefined, $event)">
      <label class="zip-rename-popover__label">
        Rename {{ renameTargetNode?.type === 'folder' ? 'folder' : 'file' }}
      </label>
      <input #renamePopoverInput
             type="text"
             class="zip-rename-popover__input"
             [ngModel]="renameDraft"
             [ngModelOptions]="{ standalone: true }"
             (ngModelChange)="updateRenameDraft($event)"
             (keydown.escape)="closeRenamePopover()"
             spellcheck="false"
             autocapitalize="off"
             autocomplete="off"
             autocorrect="off">
      <div *ngIf="renameError" class="zip-rename-popover__error">{{ renameError }}</div>
      <div class="zip-rename-popover__actions">
        <button *ngIf="renameTargetNode?.type === 'file'"
                type="button"
                class="zip-rename-popover__button zip-rename-popover__button--ghost"
                (click)="duplicateRenameTarget($event)">
          Duplicate
        </button>
        <button type="button"
                class="zip-rename-popover__button zip-rename-popover__button--ghost"
                (click)="closeRenamePopover()">
          Cancel
        </button>
        <button type="submit"
                class="zip-rename-popover__button">
          Rename
        </button>
      </div>
    </form>
  </ng-template>

  <ng-template #deletePopoverTemplate>
    <div class="zip-delete-popover">
      <label class="zip-delete-popover__label">
        Delete {{ deleteTargetNode?.type === 'folder' ? 'folder' : 'file' }}?
      </label>
      <p class="zip-delete-popover__copy">
        <ng-container *ngIf="deleteTargetNode?.type === 'folder'; else deleteFileCopy">
          Remove {{ deleteTargetNode?.name }} and everything inside it from this archive.
        </ng-container>
        <ng-template #deleteFileCopy>
          Remove {{ deleteTargetNode?.name }} from this archive.
        </ng-template>
      </p>
      <div class="zip-delete-popover__actions">
        <button type="button"
                class="zip-delete-popover__button zip-delete-popover__button--ghost"
                (click)="closeDeletePopover()">
          Cancel
        </button>
        <button type="button"
                class="zip-delete-popover__button zip-delete-popover__button--danger"
                (click)="confirmDeleteNode(undefined, $event)">
          Delete
        </button>
      </div>
    </div>
  </ng-template>
</div>
